import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import type { TrendSource } from '../../types';
import { TREND_COLORS } from '../../trendTheme';
import { useTrendAdmin } from '../TrendAdminContext';
import { joinMultiValue, splitMultiValue } from '../adminHelpers';

export default function SourceFormDialog() {
  const {
    dialogOpen,
    setDialogOpen,
    draftSource,
    setDraftSource,
    focusTagsDraft,
    setFocusTagsDraft,
    busyAction,
    handleSaveSource,
  } = useTrendAdmin();

  return (
    <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
      <DialogTitle>{draftSource._id ? 'Redigera källa' : 'Ny källa'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Namn" value={draftSource.name || ''} onChange={(e) => setDraftSource((p) => ({ ...p, name: e.target.value }))} />
          <TextField select label="Typ" value={draftSource.type || 'website'} onChange={(e) => setDraftSource((p) => ({ ...p, type: e.target.value as TrendSource['type'] }))}>
            <MenuItem value="website">Webbsida</MenuItem>
            <MenuItem value="rss">RSS</MenuItem>
            <MenuItem value="api">API</MenuItem>
            <MenuItem value="manual">Manuell</MenuItem>
          </TextField>
          <TextField label="URL" value={draftSource.url || ''} onChange={(e) => setDraftSource((p) => ({ ...p, url: e.target.value }))} />
          <TextField select label="Kategori" value={draftSource.category || 'mixed'} onChange={(e) => setDraftSource((p) => ({ ...p, category: e.target.value }))}>
            <MenuItem value="mixed">Blandat</MenuItem>
            <MenuItem value="design">Design</MenuItem>
            <MenuItem value="packaging">Förpackning</MenuItem>
            <MenuItem value="food">Mat</MenuItem>
            <MenuItem value="market">Marknad</MenuItem>
            <MenuItem value="competition">Konkurrens</MenuItem>
            <MenuItem value="consumer">Konsument</MenuItem>
          </TextField>
          <TextField label="Språk" value={draftSource.language || 'sv'} onChange={(e) => setDraftSource((p) => ({ ...p, language: e.target.value }))} />
          <TextField type="number" label="Vikt" value={draftSource.weight || 50} onChange={(e) => setDraftSource((p) => ({ ...p, weight: Number(e.target.value || 0) }))} />
          <TextField multiline minRows={2} label="Fokustaggar" helperText="Kommatecken eller radbrytningar." value={focusTagsDraft} onChange={(e) => setFocusTagsDraft(e.target.value)} />
          <TextField multiline minRows={3} label="Notering" value={draftSource.notes || ''} onChange={(e) => setDraftSource((p) => ({ ...p, notes: e.target.value }))} />
          <Box sx={{ p: 2, borderRadius: 2, bgcolor: TREND_COLORS.sectionBg, border: `1px solid ${TREND_COLORS.divider}` }}>
            <Stack spacing={1.5}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: TREND_COLORS.navy }}>
                    Riktad discovery-crawl
                  </Typography>
                  <Typography variant="caption" sx={{ color: TREND_COLORS.textMuted }}>
                    Används bara i discovery. Detta är en begränsad crawl för utvalda sajter, inte vanlig feedhämtning.
                  </Typography>
                </Box>
                <Switch
                  checked={draftSource.targetedDiscovery?.enabled === true}
                  onChange={(e) => setDraftSource((p) => ({
                    ...p,
                    targetedDiscovery: {
                      enabled: e.target.checked,
                      crawlMode: p.targetedDiscovery?.crawlMode || 'rss_only',
                      sitemapUrls: p.targetedDiscovery?.sitemapUrls || [],
                      categoryPageUrls: p.targetedDiscovery?.categoryPageUrls || [],
                      allowPatterns: p.targetedDiscovery?.allowPatterns || [],
                      denyPatterns: p.targetedDiscovery?.denyPatterns || [],
                      maxUrlsPerRun: p.targetedDiscovery?.maxUrlsPerRun || 6,
                    },
                  }))}
                />
              </Stack>
              {draftSource.targetedDiscovery?.enabled && (
                <Stack spacing={1.5}>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        select
                        fullWidth
                        label="Crawlprofil"
                        value={draftSource.targetedDiscovery?.crawlMode || 'sitemap'}
                        onChange={(e) => setDraftSource((p) => ({
                          ...p,
                          targetedDiscovery: {
                            enabled: p.targetedDiscovery?.enabled === true,
                            crawlMode: e.target.value as 'rss_only' | 'sitemap' | 'category_pages',
                            sitemapUrls: p.targetedDiscovery?.sitemapUrls || [],
                            categoryPageUrls: p.targetedDiscovery?.categoryPageUrls || [],
                            allowPatterns: p.targetedDiscovery?.allowPatterns || [],
                            denyPatterns: p.targetedDiscovery?.denyPatterns || [],
                            maxUrlsPerRun: p.targetedDiscovery?.maxUrlsPerRun || 6,
                          },
                        }))}
                      >
                        <MenuItem value="sitemap">Sitemap</MenuItem>
                        <MenuItem value="category_pages">Kategorisidor</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Max URL per discoverykörning"
                        value={draftSource.targetedDiscovery?.maxUrlsPerRun || 6}
                        onChange={(e) => setDraftSource((p) => ({
                          ...p,
                          targetedDiscovery: {
                            enabled: p.targetedDiscovery?.enabled === true,
                            crawlMode: p.targetedDiscovery?.crawlMode || 'sitemap',
                            sitemapUrls: p.targetedDiscovery?.sitemapUrls || [],
                            categoryPageUrls: p.targetedDiscovery?.categoryPageUrls || [],
                            allowPatterns: p.targetedDiscovery?.allowPatterns || [],
                            denyPatterns: p.targetedDiscovery?.denyPatterns || [],
                            maxUrlsPerRun: Number(e.target.value || 0),
                          },
                        }))}
                      />
                    </Grid>
                  </Grid>
                  {(draftSource.targetedDiscovery?.crawlMode || 'sitemap') === 'sitemap' ? (
                    <TextField
                      multiline
                      minRows={3}
                      label="Sitemap-URL:er"
                      helperText="Kommatecken eller radbrytningar. Ange en eller flera XML-sitemaps."
                      value={joinMultiValue(draftSource.targetedDiscovery?.sitemapUrls || [])}
                      onChange={(e) => setDraftSource((p) => ({
                        ...p,
                        targetedDiscovery: {
                          enabled: p.targetedDiscovery?.enabled === true,
                          crawlMode: p.targetedDiscovery?.crawlMode || 'sitemap',
                          sitemapUrls: splitMultiValue(e.target.value),
                          categoryPageUrls: p.targetedDiscovery?.categoryPageUrls || [],
                          allowPatterns: p.targetedDiscovery?.allowPatterns || [],
                          denyPatterns: p.targetedDiscovery?.denyPatterns || [],
                          maxUrlsPerRun: p.targetedDiscovery?.maxUrlsPerRun || 6,
                        },
                      }))}
                    />
                  ) : (
                    <TextField
                      multiline
                      minRows={3}
                      label="Kategorisidor"
                      helperText="Kommatecken eller radbrytningar. Dessa sidor skördas efter artikellänkar."
                      value={joinMultiValue(draftSource.targetedDiscovery?.categoryPageUrls || [])}
                      onChange={(e) => setDraftSource((p) => ({
                        ...p,
                        targetedDiscovery: {
                          enabled: p.targetedDiscovery?.enabled === true,
                          crawlMode: p.targetedDiscovery?.crawlMode || 'category_pages',
                          sitemapUrls: p.targetedDiscovery?.sitemapUrls || [],
                          categoryPageUrls: splitMultiValue(e.target.value),
                          allowPatterns: p.targetedDiscovery?.allowPatterns || [],
                          denyPatterns: p.targetedDiscovery?.denyPatterns || [],
                          maxUrlsPerRun: p.targetedDiscovery?.maxUrlsPerRun || 6,
                        },
                      }))}
                    />
                  )}
                  <TextField
                    multiline
                    minRows={2}
                    label="Tillåt mönster"
                    helperText="Valfritt. URL måste matcha minst ett mönster om listan inte är tom."
                    value={joinMultiValue(draftSource.targetedDiscovery?.allowPatterns || [])}
                    onChange={(e) => setDraftSource((p) => ({
                      ...p,
                      targetedDiscovery: {
                        enabled: p.targetedDiscovery?.enabled === true,
                        crawlMode: p.targetedDiscovery?.crawlMode || 'sitemap',
                        sitemapUrls: p.targetedDiscovery?.sitemapUrls || [],
                        categoryPageUrls: p.targetedDiscovery?.categoryPageUrls || [],
                        allowPatterns: splitMultiValue(e.target.value),
                        denyPatterns: p.targetedDiscovery?.denyPatterns || [],
                        maxUrlsPerRun: p.targetedDiscovery?.maxUrlsPerRun || 6,
                      },
                    }))}
                  />
                  <TextField
                    multiline
                    minRows={2}
                    label="Blockera mönster"
                    helperText="Valfritt. Exempel: /tag/, /author/ eller regex:^https://example.com/news/"
                    value={joinMultiValue(draftSource.targetedDiscovery?.denyPatterns || [])}
                    onChange={(e) => setDraftSource((p) => ({
                      ...p,
                      targetedDiscovery: {
                        enabled: p.targetedDiscovery?.enabled === true,
                        crawlMode: p.targetedDiscovery?.crawlMode || 'sitemap',
                        sitemapUrls: p.targetedDiscovery?.sitemapUrls || [],
                        categoryPageUrls: p.targetedDiscovery?.categoryPageUrls || [],
                        allowPatterns: p.targetedDiscovery?.allowPatterns || [],
                        denyPatterns: splitMultiValue(e.target.value),
                        maxUrlsPerRun: p.targetedDiscovery?.maxUrlsPerRun || 6,
                      },
                    }))}
                  />
                </Stack>
              )}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDialogOpen(false)}>Avbryt</Button>
        <Button variant="contained" disabled={busyAction === 'saveSource'} onClick={() => void handleSaveSource()}>Spara</Button>
      </DialogActions>
    </Dialog>
  );
}
